import type { ClassifierData } from 'types/classifiers';
import { getAllTranslatedMessages } from 'utils/getTranslations';

const UNMASK_SELECTOR = '[data-ph-unmask]';
// PostHog's built-in maskTextClass, so a subtree nested inside an unmasked one can be masked again.
const REMASK_SELECTOR = '.ph-mask';

const TRANSMITTABLE_ATTRIBUTES = new Set(['title', 'alt', 'aria-label', 'aria-description', 'placeholder', 'download']);

let staticCopy: Set<string> | undefined;
let classifierCopy = new Set<string>();

// Only the app's own translated copy may reach the recorder, matched verbatim against the catalog.
// Messages holding ICU placeholders or rich tags are resolved at render time, so the rendered text
// never equals its catalog entry and stays masked along with the values interpolated into it.
const getStaticCopy = (): Set<string> => {
    staticCopy ??= new Set(
        getAllTranslatedMessages()
            .map((message) => message.trim())
            .filter((message) => message.length > 0 && !/[{<]/.test(message))
    );

    return staticCopy;
};

// Classifier text is tenant-configured reference data — statuses, types, roles, titles — rendered as
// the app's own vocabulary rather than as a record's content, so it joins the allowlist as it loads.
// Labels only: a value's description is free text that no dropdown or chip needs to be readable.
export const registerClassifierCopy = (classifiers: ClassifierData[]): void => {
    classifierCopy = new Set(
        classifiers
            .flatMap((classifier) => classifier.values ?? [])
            .flatMap((value) => value.texts)
            .map((text) => text.value.trim())
            .filter(Boolean)
    );
};

// Zero-width characters carry no content but are not whitespace to a regex, so masking them would
// paint a visible asterisk where the layout expects nothing. They are left alone, and ignored when matching against the catalog.
const INVISIBLE_CHARACTERS = '\\u200b-\\u200f\\u2060\\ufeff';
const invisible = new RegExp(`[${INVISIBLE_CHARACTERS}]`, 'g');
const maskable = new RegExp(`[^\\s${INVISIBLE_CHARACTERS}]`, 'g');

export const maskValue = (value: string): string => value.replace(maskable, '*');

const isTransmittable = (value: string, element?: Element | null): boolean => {
    const marker = element?.closest(`${UNMASK_SELECTOR},${REMASK_SELECTOR}`);

    if (marker) {
        return marker.matches(UNMASK_SELECTOR);
    }

    const text = value.replace(invisible, '').trim();

    return getStaticCopy().has(text) || classifierCopy.has(text);
};

export const maskTextFn = (text: string, element?: HTMLElement): string =>
    isTransmittable(text, element) ? text : maskValue(text);

// A same-origin target holds nothing the recorder does not already get from the page URL, while an
// external one carries e-mail addresses (`mailto:`), document links and integration URLs, none of
// which the whitelist permits. The origin is resolved rather than prefix-matched, so a target the
// browser reads as protocol-relative — `/\evil.example`, where the backslash normalises to a slash
// — lands on the external side. An unmask marker does not open these up: markers are placed for the
// text a subtree renders, not for what its links point at.
const isSameOrigin = (value: string): boolean => {
    try {
        return new URL(value, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
};

export const maskAttributeFn = (name: string, value: string, element?: Element): string => {
    if (name === 'href') {
        return isSameOrigin(value) ? value : maskValue(value);
    }

    return TRANSMITTABLE_ATTRIBUTES.has(name) && !isTransmittable(value, element) ? maskValue(value) : value;
};
